import { useEffect, useRef, useState } from 'react';
import { api, type Brand, type Card, type CardStep, type CastMember, type IdeaTurn, type Idea, type Storyline } from '../lib/api';
import { titleFrom, isEnough, MIN_IDEA, MAX_IDEA } from '../lib/newIdea';

// The Idea Lab (200): where an idea is born.
//
// The studio is a row of benches, each of which can make one kind of thing
// from a field that can only mean one thing. That is safe and it is why a
// creator with an idea has nowhere to put it. This is the one place they can
// say what they want in their own words.
//
// IT OPENS EMPTY. The first version picked the most recent project and showed
// its thread, which made this a workbench with somebody else's work already on
// it — a creator arriving with a new idea had to clear the table first, and
// what they typed read as a note on last week's episode. Nothing is selected,
// the thread is blank, and the first sentence typed here STARTS something.
// Work already under way lives on the board, and is reached from one line at
// the bottom that has to be asked for.
//
// The director answers with a single proposal, and the proposal is the whole
// contract: what would be made, and where. Accept is a JUMP, not a spend — it
// opens the bench that owns the work with everything filled in, and the quote
// and the confirmation stay where they already live. One place money is ever
// agreed to.
//
// A dismissed proposal stays on the thread, greyed. Director.tsx settled that
// argument already: a suggestion that vanishes takes with it the only evidence
// the assistant ever made it.

/** Ideas, not follow-ups: every one of these could be the first thing typed. */
const EXAMPLES = [
  'how to raise a dragon, from the egg to its first flight',
  'a squirrel family and one very bad Monday morning',
  'what nobody tells you about your first week with a puppy',
];

export function IdeaLab({ brand, cards, onGo, onError, onCardMade }: {
  brand: Brand | null;
  cards: Card[];
  /** A project started here, handed up so the studio's list has it too. */
  onCardMade: (c: Card) => void;
  /** Accept lands on a bench, lights a control, and hands it the prefill. */
  onGo: (bench: string, move: string | null, prefill: Idea | null) => void;
  onError: (m: string | null) => void;
}) {
  const mine = cards.filter((c) => !brand || c.brand_id === brand.id);
  const [cardId, setCardId] = useState<string>('');
  const [steps, setSteps] = useState<CardStep[]>([]);
  // Who is in this episode, and who else is on the roster. An episode with
  // nobody in it is the normal state of a new idea, and it is the first
  // question — a dragon film planned against a cast of squirrels is what
  // happens when nobody asks it.
  const [cast, setCast] = useState<CastMember[]>([]);
  const [roster, setRoster] = useState<CastMember[]>([]);
  const [casting, setCasting] = useState(false);
  // The story, which is the first thing that exists. The characters are read
  // off it, and the cast is made from the ones the creator keeps.
  const [story, setStory] = useState<Storyline | null>(null);
  const [uncast, setUncast] = useState<string[]>([]);
  // What making the cast did about faces: the library's pictures were taken
  // where a name matched, and the rest are named so Draw is one visit.
  const [castNote, setCastNote] = useState<string | null>(null);
  const [keep, setKeep] = useState<Set<string>>(new Set());
  const [writing, setWriting] = useState(false);
  const [nothing, setNothing] = useState<string | null>(null);
  const [turns, setTurns] = useState<IdeaTurn[]>([]);
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState(false);
  // A project is made HERE. Before this, the only way to start one was the
  // Flight Deck, which meant the screen built for having an idea could work
  // on every episode except a new one.
  const [making, setMaking] = useState(false);
  // The blank page's own box, and the line that reveals work already started.
  const [seed, setSeed] = useState('');
  const [openOld, setOpenOld] = useState(false);
  const foot = useRef<HTMLDivElement | null>(null);

  const card = mine.find((c) => c.id === cardId) ?? null;
  // Deliberately no "pick the most recent one". An empty cardId IS the blank
  // page, and the only things that fill it are starting something or asking
  // for what is already under way.
  useEffect(() => {
    if (cardId && !mine.some((c) => c.id === cardId)) { setCardId(''); setTurns([]); setSteps([]); }
  }, [mine.map((c) => c.id).join(','), cardId]);

  async function load(id: string) {
    try {
      const [n, t] = await Promise.all([api.cardNext(id), api.ideaTurns(id)]);
      setSteps(n.steps); setNothing(n.nothing); setTurns(t);
      setCast(n.cast); setRoster(n.roster);
      setStory(n.storyline); setUncast(n.uncast);
      setKeep(new Set(n.uncast.map((x) => x.toLowerCase())));
    } catch (e) { onError((e as Error).message); }
  }
  useEffect(() => { if (cardId) void load(cardId); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [cardId]);
  useEffect(() => { foot.current?.scrollIntoView({ block: 'end' }); }, [turns.length]);

  async function send(text: string) {
    if (!cardId || !text.trim() || busy) return;
    setBusy(true); onError(null);
    // Shown immediately: the creator should see their own words land before
    // the director has thought about them.
    setTurns((t) => [...t, { id: -1, role: 'creator', text: text.trim(), proposal: null, status: 'none', at: '' }]);
    setAsk('');
    try {
      await api.askIdea(cardId, text.trim());
      await load(cardId);
    } catch (e) { onError((e as Error).message); await load(cardId); } finally { setBusy(false); }
  }

  /**
   * An idea becomes a project, and the idea itself becomes the first thing
   * said about it.
   *
   * The title is derived rather than asked for: "what is it called?" is the
   * wrong question at the moment an idea arrives, and a name is a second's
   * work on the board later. What matters is that the creator's own sentence
   * is what the director answers — everything after this is driven by it.
   */
  async function startFrom(idea: string, title?: string): Promise<boolean> {
    const text = idea.replace(/\s+/g, ' ').trim();
    if (!isEnough(text) || making) return false;
    setMaking(true); onError(null);
    try {
      const card = await api.createCard({
        title: (title?.trim() || titleFrom(text)).slice(0, 200),
        brandId: brand?.id ?? null,
        topic: text.slice(0, 200),
      });
      onCardMade(card);
      setCardId(card.id); setSeed(''); setTurns([]); setSteps([]); setCast([]); setRoster([]); setStory(null);
      // THE STORY IS THE FIRST THING. Not a shot plan, not a cast: what
      // happens, who it happens to, how it ends. Everything after it is
      // derived from this, and the characters come out of it.
      setWriting(true);
      try {
        const written = await api.writeStoryline(card.id, text);
        setStory(written);
        setKeep(new Set(written.characters.map((c) => c.name.toLowerCase())));
      } catch (e) { onError((e as Error).message); } finally { setWriting(false); }
      await load(card.id);
      return true;
    } catch (e) { onError((e as Error).message); return false; } finally { setMaking(false); }
  }

  /** Make the story's people real, and put them in this episode. */
  async function makeTheCast() {
    if (!story || casting) return;
    const wanted = story.characters.filter((c) => keep.has(c.name.toLowerCase()));
    if (!wanted.length) return;
    setCasting(true); onError(null);
    try {
      const r = await api.castFromStory(cardId, wanted.map((c) => ({ name: c.name, sheet: c.sheet, role: c.role })));
      const fromLibrary = r.cast.filter((c) => c.portrait === 'library').map((c) => c.name);
      const faceless = r.cast.filter((c) => c.portrait === 'none').map((c) => c.name);
      setCastNote([
        fromLibrary.length ? `${fromLibrary.join(', ')} took ${fromLibrary.length === 1 ? 'a portrait' : 'portraits'} from the library.` : '',
        faceless.length ? `${faceless.join(', ')} ${faceless.length === 1 ? 'has' : 'have'} no picture yet — draw ${faceless.length === 1 ? 'it' : 'them'} on Talent, or file one under their name.` : '',
      ].filter(Boolean).join(' ') || null);
      await load(cardId);
    } catch (e) { onError((e as Error).message); } finally { setCasting(false); }
  }

  /** Write it again from the same idea. A story is cheap; a wrong one is not. */
  async function rewrite() {
    const idea = story?.idea || card?.title || '';
    if (!idea || writing) return;
    setWriting(true); onError(null);
    try {
      const written = await api.writeStoryline(cardId, idea);
      setStory(written);
      setKeep(new Set(written.characters.map((c) => c.name.toLowerCase())));
      await load(cardId);
    } catch (e) { onError((e as Error).message); } finally { setWriting(false); }
  }

  /** Say who is in it. The whole list every time, so removing is the same call. */
  async function setThisCast(ids: string[]) {
    if (!cardId || casting) return;
    setCasting(true); onError(null);
    try {
      await api.setCardCast(cardId, ids);
      await load(cardId);
    } catch (e) { onError((e as Error).message); } finally { setCasting(false); }
  }

  async function decide(turn: IdeaTurn, accept: boolean) {
    try {
      await api.decideIdea(cardId, turn.id, accept ? 'accept' : 'dismiss');
      // A new episode is the one accept that resolves here: it is made, it
      // becomes the project in the selector, and the creator keeps talking to
      // the same director about it.
      if (accept && turn.proposal?.action === 'start_episode') {
        const title = String(turn.proposal.args.title ?? '');
        const made = await startFrom(String(turn.proposal.args.topic ?? '') || title, title);
        if (made) return;
      }
      if (accept && turn.proposal?.bench) onGo(turn.proposal.bench, turn.proposal.move, turn.proposal);
      await load(cardId);
    } catch (e) { onError((e as Error).message); }
  }

  // THE BLANK PAGE. Nothing chosen, nothing on the table, one question.
  if (!cardId) {
    return (
      <section data-tool="idea-lab" style={{ display: 'grid', gap: 14, maxWidth: 760, width: '100%', margin: '0 auto' }}>
        <div style={{ display: 'grid', gap: 6 }}>
          <h3 style={{ margin: 0 }}>What do you want to make?</h3>
          <span className="sub-dim" style={{ fontSize: 13 }}>
            In your own words — a sentence, or a whole brief. The story gets written first — what happens,
            who it happens to, how it ends — and the characters come out of the story.
          </span>
        </div>

        <textarea value={seed} rows={3} maxLength={MAX_IDEA} autoFocus
                  data-move="idea-seed"
                  placeholder="How to raise a dragon, from the egg to its first flight."
                  onChange={(e) => setSeed(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void startFrom(seed); }
                  }} />

        <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn pri" disabled={making || !isEnough(seed)}
                  onClick={() => void startFrom(seed)}>
            {making ? 'Writing the story…' : 'Write the story'}
          </button>
          <span className="sub-dim" style={{ fontSize: 12 }}>
            {isEnough(seed)
              ? `It will be called "${titleFrom(seed)}" — rename it any time.`
              : `A few words is enough (${MIN_IDEA} characters).`}
          </span>
        </div>

        {/* Examples are ideas, not follow-ups: any one of them could be the
            first thing ever typed into this workspace. */}
        <div style={{ display: 'grid', gap: 6 }}>
          <span className="sub-dim" style={{ fontSize: 12 }}>Or start from one of these:</span>
          {EXAMPLES.map((x) => (
            <button key={x} type="button" className="btn tiny" disabled={making}
                    style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    onClick={() => void startFrom(x)}>{x}</button>
          ))}
        </div>

        {/* Work already under way. Never preselected, and never above the
            blank page: this screen is for the idea that has not been had yet.
            Everything here is also on the Flight Deck. */}
        {mine.length > 0 && (
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10, display: 'grid', gap: 6 }}>
            {!openOld
              ? (
                <button type="button" className="btn tiny" data-move="idea-resume"
                        style={{ justifySelf: 'start' }} onClick={() => setOpenOld(true)}>
                  Or pick up something you started ({mine.length})
                </button>
              )
              : (
                <div className="row wrap" style={{ gap: 6 }}>
                  {mine.slice(0, 8).map((c) => (
                    <button key={c.id} type="button" className="btn tiny"
                            style={{ textAlign: 'left' }} onClick={() => setCardId(c.id)}>
                      {c.title.length > 44 ? `${c.title.slice(0, 44)}…` : c.title}
                      <span className="sub-dim" style={{ marginLeft: 6, fontSize: 11 }}>{c.stage}</span>
                    </button>
                  ))}
                </div>
              )}
          </div>
        )}
      </section>
    );
  }

  return (
    <section data-tool="idea-lab" style={{ display: 'grid', gap: 12, maxWidth: 880, width: '100%', margin: '0 auto' }}>
      <div className="row wrap" style={{ gap: 10, alignItems: 'baseline' }}>
        <h3 style={{ margin: 0 }}>{card?.title ?? 'Idea Lab'}</h3>
        {card && <span className="chip" style={{ fontSize: 11 }}>{card.stage}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn tiny" data-move="idea-new"
                onClick={() => { setCardId(''); setTurns([]); setSteps([]); setCast([]); setOpenOld(false); }}>
          + New idea
        </button>
      </div>

      {writing && (
        <p className="sub-dim" style={{ margin: 0, fontSize: 13 }} data-tool="idea-writing">
          Writing the story — what happens, who it happens to, how it ends…
        </p>
      )}

      {/* THE STORY. The first thing that exists, and the thing everything
          else is derived from. It is shown whole, because a creator cannot
          judge an ending they have not been told. */}
      {story && !writing && (
        <div style={{ display: 'grid', gap: 10, border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}
             data-tool="idea-story" data-move="idea-story">
          <div className="row wrap" style={{ gap: 8, alignItems: 'baseline' }}>
            <b style={{ fontSize: 14 }}>The story</b>
            <span style={{ flex: 1 }} />
            <button type="button" className="btn tiny" disabled={writing} onClick={() => void rewrite()}>
              Write it again
            </button>
          </div>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5 }}>{story.logline}</p>

          <div style={{ display: 'grid', gap: 6 }}>
            {story.arc.map((a) => (
              <div key={a.part} style={{ display: 'grid', gap: 2 }}>
                <span className="sub-dim" style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase' }}>{a.part}</span>
                <span style={{ fontSize: 13, lineHeight: 1.5 }}>{a.what}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gap: 4 }}>
            <span className="sub-dim" style={{ fontSize: 12 }}>{story.beats.length} beats</span>
            <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 3 }}>
              {story.beats.map((b, i) => (
                <li key={`${b.beat}-${i}`} style={{ fontSize: 13, lineHeight: 1.45 }}>
                  <b>{b.beat}</b>{b.what ? ` — ${b.what}` : ''}
                </li>
              ))}
            </ol>
          </div>

          {story.ending && (
            <span className="sub-dim" style={{ fontSize: 12 }}>Ends on: {story.ending}</span>
          )}

          {/* The characters the STORY needs. Nobody was chosen in advance;
              these exist because something in the story has to be done by
              somebody. Untick anyone the story can do without. */}
          {story.characters.length > 0 && (
            <div style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
              <span className="sub-dim" style={{ fontSize: 12 }}>
                Who the story needs — {uncast.length ? 'none of these exist yet' : 'all made'}
              </span>
              {story.characters.map((c) => {
                const k = c.name.toLowerCase();
                const already = !uncast.some((u) => u.toLowerCase() === k);
                return (
                  <label key={c.name} style={{ display: 'grid', gap: 2, gridTemplateColumns: '18px 1fr', alignItems: 'start' }}>
                    <input type="checkbox" checked={already || keep.has(k)} disabled={already || casting}
                           onChange={(e) => setKeep((was) => {
                             const next = new Set(was);
                             if (e.target.checked) next.add(k); else next.delete(k);
                             return next;
                           })} />
                    <span style={{ display: 'grid', gap: 1 }}>
                      <span style={{ fontSize: 13 }}>
                        <b>{c.name}</b>
                        {c.role && <span className="sub-dim"> — {c.role}</span>}
                        {already && <span className="sub-dim" style={{ marginLeft: 6, fontSize: 11 }}>made</span>}
                      </span>
                      <span className="sub-dim" style={{ fontSize: 12, lineHeight: 1.45 }}>{c.sheet}</span>
                    </span>
                  </label>
                );
              })}
              {castNote && <span className="sub-dim" style={{ fontSize: 12 }} data-tool="idea-cast-note">{castNote}</span>}
              {uncast.length > 0 && (
                <button type="button" className="btn pri tiny" data-move="idea-make-cast"
                        style={{ justifySelf: 'start' }} disabled={casting || keep.size === 0}
                        onClick={() => void makeTheCast()}>
                  {casting ? 'Making…' : `Make ${keep.size === 1 ? 'this character' : `these ${keep.size} characters`}`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* WHO IS IN IT. The first question about a new idea, and the gate in
          front of anything that draws a face. It sits above the director's
          steps because it is the one thing that cannot be deferred: a shot is
          held together by a character, and a character nobody chose is a
          character the model invents again every scene. */}
      <div style={{ display: 'grid', gap: 6 }} data-tool="idea-cast" data-move="idea-cast">
        <span className="sub-dim" style={{ fontSize: 12 }}>
          {cast.length ? 'Who is in it' : 'Who is in it? Nothing can be shot until this is answered.'}
        </span>
        <div className="row wrap" style={{ gap: 6 }}>
          {roster.map((m) => {
            const on = cast.some((c) => c.id === m.id);
            return (
              <button key={m.id} type="button" className={`btn tiny${on ? ' pri' : ''}`} disabled={casting}
                      title={m.hasPortrait ? (on ? 'In this episode' : 'Put them in this episode') : `${m.name} has no portrait yet — draw one on Talent`}
                      onClick={() => void setThisCast(on ? cast.filter((c) => c.id !== m.id).map((c) => c.id)
                                                        : [...cast.map((c) => c.id), m.id])}>
                {m.name}
                {!m.hasPortrait && <span className="sub-dim" style={{ marginLeft: 5, fontSize: 10 }}>no portrait</span>}
              </button>
            );
          })}
          <button type="button" className="btn tiny" data-move="idea-new-character"
                  title="A character is a name, a sheet and a portrait. The sheet is what fixes how they look, permanently."
                  onClick={() => onGo('talent', 'talent-face', null)}>
            + New character
          </button>
        </div>
        {cast.length === 0 && roster.length > 0 && (
          <span className="sub-dim" style={{ fontSize: 12 }}>
            The names above are everyone on this workspace. They belong to other shows until you put them in this one.
          </span>
        )}
      </div>

      {/* What the director thinks about THIS project. No model asked: every
          line is a fact the card already carries. */}
      <div style={{ display: 'grid', gap: 6 }} data-tool="idea-steps">
        <span className="sub-dim" style={{ fontSize: 12 }}>What the director thinks</span>
        {nothing && <span className="sub-dim" style={{ fontSize: 13 }}>{nothing}</span>}
        <div className="row wrap" style={{ gap: 6 }}>
          {steps.map((s) => (
            <button key={s.id} type="button" className="btn tiny" data-move={s.id}
                    title={s.why} onClick={() => onGo(s.bench, s.move, null)}
                    style={{ borderColor: s.blocked ? 'var(--accent)' : undefined, textAlign: 'left' }}>
              {s.title}
            </button>
          ))}
        </div>
      </div>

      {/* The thread. */}
      <div style={{ display: 'grid', gap: 10, borderTop: '1px solid var(--line)', paddingTop: 12, maxHeight: 420, overflowY: 'auto' }}
           data-tool="idea-thread">
        {turns.length === 0 && (
          <span className="sub-dim" style={{ fontSize: 13 }}>
            Nothing said about this one yet. Ask for what it needs next.
          </span>
        )}
        {turns.map((t, i) => {
          const p = t.proposal;
          const dim = t.status === 'dismissed';
          return (
            <div key={`${t.id}-${i}`} style={{ display: 'grid', gap: 4, opacity: dim ? 0.5 : 1 }}>
              <span className="sub-dim" style={{ fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                {t.role === 'creator' ? 'you' : 'the director'}
              </span>
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>{t.text}</span>
              {p && t.status === 'open' && p.bench && (
                <div className="row wrap" style={{ gap: 6, marginTop: 2 }}>
                  <button type="button" className="btn tiny pri" onClick={() => void decide(t, true)}>
                    {p.action === 'start_episode' ? 'Start it'
                      : p.inline ? 'Do it' : `Open it in the ${benchName(p.bench)}`}
                  </button>
                  <button type="button" className="btn tiny" onClick={() => void decide(t, false)}>Not now</button>
                </div>
              )}
              {t.status === 'dismissed' && <span className="sub-dim" style={{ fontSize: 11 }}>put aside</span>}
              {t.status === 'accepted' && <span className="sub-dim" style={{ fontSize: 11 }}>taken up</span>}
            </div>
          );
        })}
        <div ref={foot} />
      </div>

      <div className="row" style={{ gap: 6 }}>
        <input value={ask} maxLength={1200} placeholder="Ask the director for something…"
               onChange={(e) => setAsk(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(ask); } }}
               style={{ flex: 1 }} data-move="idea-ask" />
        <button type="button" className="btn pri" disabled={busy || ask.trim().length < 2} onClick={() => void send(ask)}>
          {busy ? 'Thinking…' : 'Ask'}
        </button>
      </div>
      <span className="sub-dim" style={{ fontSize: 12 }}>
        Nothing is made until you press again. The director proposes; the bench that does the work is where the price is.
      </span>
    </section>
  );
}

function benchName(b: string): string {
  return ({ video: 'Video Lab', audio: 'Audio Lab', lab: 'Lab', talent: 'Talent',
            assets: 'library', deck: 'Flight Deck', schedule: 'Schedule',
            connectors: 'Connectors' } as Record<string, string>)[b] ?? b;
}
